import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from '../../../../../../shared/services/helpers/message-service/message.service';
import { ShootProofDataProvider } from '../../../../../../shared/services/integrations/shootProof/shootProof.data.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DeleteGalleryModal } from '@web-components/integrations/connect-galleries/modals/delete-gallery-modal/delete-gallery.modal';
import { Subject } from 'rxjs/Subject';
import { AddGalleryModal } from '@web-components/integrations/connect-galleries/modals/add-gallery-modal/add-gallery.modal';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import {
	BootstrapSettings,
	GeneralAction,
	PicTimeGalleriesResponse,
	ServerDataResponse
} from '@models/models';
import { SettingsProvider } from '@services/settings/settings';
import { IntegrationsServiceProvider } from '@services/integrations/integrations.service';

@Component({
	selector: 'gallery-view-block',
	template: require('./gallery-view-block.component.html'),
	styles: [require('./gallery-view-block.component.scss')]
})
export class GalleryViewBlockComponent implements OnInit, OnDestroy {
	@Input('job') job: any;
	resultArrayGalleries = [];
	shootProofGalleries = [];
	picTimeGalleries = [];
	galleryIdForDelete: number;
	shootProofBrands = [];
	jobId: number;
	availableActionsForGallery = null;
	mainShootDate: string;
	jobName: string;
	shootProofActions = [];
	picTimeActions = [];
	shootProofState: string;
	picTimeState: string;
	picTimeAvailable: boolean;
	deleteGalleryModalRef: NgbModalRef;
	addGalleryModalRef: NgbModalRef;
	syncingGallery: number = null;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private shootProofDataService: ShootProofDataProvider,
		private pickTimeDataProvider: PickTimeDataProvider,
		private messageService: MessageService,
		private modalService: SNModalService,
		private settingsService: SettingsProvider,
		private integrationService: IntegrationsServiceProvider
	) {}

	ngOnInit(): void {
		this.isPicTimeAvailable();
		this.setVariables();
		this.getShootProofGalleries();
		this.getPicTimeActions();
		this.shouldUpdateGalleriesObservable();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.integrationService.destroyGalleriesUpdatedObservable();
	}

	private shouldUpdateGalleriesObservable(): void {
		this.integrationService.shouldGalleriesBeUpdatedObservable
			.pipe(
				takeUntil(this.unsubscribe),
				filter(value => !!value && value === 'UPDATE_SN_GALLERIES')
			)
			.subscribe(() => this.refreshResultArray());
	}

	isPicTimeAvailable(): void {
		this.settingsService.timeFormatSettings
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: BootstrapSettings) => {
				if (data.picTimeAvailable) {
					this.picTimeAvailable = true;
				}
			});
	}

	getShootProofGalleries(): void {
		this.shootProofDataService
			.getJobGalleriesV1(this.jobId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				data => this.successGetShootProofGalleries(data),
				error => this.messageService.handleError(error)
			);
	}

	getPicTimeGalleries(): void {
		this.pickTimeDataProvider
			.getPicTimeGalleries(this.jobId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data: ServerDataResponse<PicTimeGalleriesResponse[]>): void => {
					this.picTimeGalleries = data.data;
					this.resultArrayGalleries.push(...data.data);
				},
				(error): void => this.messageService.handleError(error)
			);
	}

	refreshResultArray(): void {
		this.resultArrayGalleries = [];
		this.getShootProofGalleries();
		this.getPicTimeActions();
	}

	getPicTimeActions() {
		this.pickTimeDataProvider
			.getPicTimeActions(this.jobId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data): void => this.successGetPicTimeActions(data),
				error => this.messageService.handleError(error)
			);
	}

	synchronizePicTimeGallery(action: GeneralAction, index: number): void {
		if (action) {
			const { url, method } = action;
			this.syncingGallery = index;
			this.pickTimeDataProvider
				.syncPicTimeGallery(url, method)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize((): boolean => (this.syncingGallery = null))
				)
				.subscribe(
					(): void => {
						this.messageService.showToaster('s_7', 'picTime');
						this.refreshResultArray();
					},
					(error): void => this.messageService.handleError(error)
				);
		}
	}

	checkGalleriesActions(): string {
		if (this.shootProofActions.length && this.picTimeActions.length) {
			return 'CONNECTED TO PIC-TIME & SHOOTPROOF';
		} else if (this.shootProofActions.length) {
			return 'CONNECTED TO SHOOTPROOF';
		} else if (this.picTimeActions.length) {
			return 'CONNECTED TO PIC-TIME';
		}
		return '';
	}

	private successGetShootProofGalleries(data): void {
		if (data.data.shootProofGalleryLayout) {
			const { avalibleActions, galleries } = data.data.shootProofGalleryLayout;
			this.shootProofActions = avalibleActions.reverse();
			this.shootProofGalleries = galleries;
			this.resultArrayGalleries.push(...galleries);
		} else {
			this.shootProofActions = [];
			this.shootProofGalleries = [];
		}
		this.shootProofState = data.data.state;
	}

	private successGetPicTimeActions(data): void {
		if (data.data.ptGalleryLayout) {
			const { availableActions } = data.data.ptGalleryLayout;
			this.picTimeActions = availableActions.reverse();
		} else {
			this.picTimeActions = [];
		}
		if (data.data.state === 'ENABLED') {
			this.getPicTimeGalleries();
		}
		this.picTimeState = data.data.state;
	}

	showDeleteModal(gallery): void {
		this.availableActionsForGallery = gallery.availableActions;
		this.galleryIdForDelete = gallery.id;
		this.deleteGalleryModalRef = this.modalService.openModal(
			DeleteGalleryModal,
			{
				windowClass: 'ng-bootstrap-modal__small gallery-delete-modal'
			}
		);
		this.deleteGalleryModalRef.componentInstance.deleteGalleryId = gallery.id;
		this.deleteGalleryModalRef.componentInstance.actions =
			gallery.availableActions;
		this.deleteGalleryModalRef.componentInstance.picTimeActions =
			gallery.actions;
	}

	showAddGalleryModal(): void {
		this.addGalleryModalRef = this.modalService.openModal(AddGalleryModal, {
			windowClass: 'ng-bootstrap-modal__small'
		});
		this.addGalleryModalRef.componentInstance.shootProofActions = this.shootProofActions;
		this.addGalleryModalRef.componentInstance.picTimeActions = this.picTimeActions;
		this.addGalleryModalRef.componentInstance.shootProofBrands = this.shootProofBrands;
		this.addGalleryModalRef.componentInstance.jobId = this.jobId;
		this.addGalleryModalRef.componentInstance.mainShootDate = this.mainShootDate;
		this.addGalleryModalRef.componentInstance.jobName = this.jobName;
	}

	private setVariables(): void {
		const { id, name, finishDate } = this.job;
		this.jobId = id;
		this.jobName = name;
		this.mainShootDate = finishDate;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/integrations/connect-galleries/gallery-view-block/gallery-view-block.component.ts